import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Heart, Loader2, Sparkles, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import useAiFeatureStatus from '../hooks/useAiFeatureStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Ask-about-your-breed Q&A panel. Gated by `ai_health_insights` feature flag.
 * Shows a friendly "coming soon" notice when the feature is OFF.
 */
export default function BreedHealthInsightsPanel({ defaultBreed = '' }) {
  const { flags, loading: statusLoading } = useAiFeatureStatus();
  const [breed, setBreed] = useState(defaultBreed);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);

  const handleAsk = async () => {
    if (!breed.trim() || question.trim().length < 5) {
      toast.error('Enter a breed and a question (min 5 chars).');
      return;
    }
    setBusy(true);
    setAnswer('');
    try {
      const res = await axios.post(
        `${API}/ai/health-qa`,
        { breed: breed.trim(), question: question.trim() },
        { withCredentials: true }
      );
      setAnswer(res.data.answer);
    } catch (err) {
      if (err?.response?.status === 503) {
        toast.error('Health Insights is currently disabled.');
      } else if (err?.response?.status === 401) {
        toast.error('Please sign in to ask questions.');
      } else {
        toast.error(err?.response?.data?.detail || 'Ask failed');
      }
    } finally {
      setBusy(false);
    }
  };

  if (statusLoading) return null;

  if (!flags.ai_health_insights) {
    return (
      <Card data-testid="breed-health-insights-disabled">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-rose-500" />
            Ask About Your Breed
          </CardTitle>
          <CardDescription>
            Breed-specific health and behavior Q&amp;A — coming soon. Unlocks at 500 members.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card data-testid="breed-health-insights-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" />
          Ask About Your Breed
        </CardTitle>
        <CardDescription>
          Get breed-specific answers on health, behavior, and training. Powered by
          Claude — always consult your vet for anything urgent.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="breed-input">Breed</Label>
          <Input
            id="breed-input"
            value={breed}
            onChange={(e) => setBreed(e.target.value)}
            placeholder="e.g., Labrador Retriever"
            data-testid="breed-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="question-input">Your question</Label>
          <Textarea
            id="question-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
            placeholder="What joint problems should I watch for in a senior Lab?"
            data-testid="question-input"
          />
        </div>
        <Button
          onClick={handleAsk}
          disabled={busy}
          className="w-full"
          data-testid="ask-breed-btn"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Asking Claude…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Ask
            </>
          )}
        </Button>

        {answer && (
          <div
            className="p-4 rounded bg-amber-50 border border-amber-200 whitespace-pre-wrap text-sm"
            data-testid="breed-answer"
          >
            {answer}
          </div>
        )}

        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>
            Answers are AI-generated for educational purposes and do not replace
            professional veterinary care.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
